import React, { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useRouter } from 'next/router'
import ReactGA from 'react-ga4'

import DeckService from 'services/deck.service'
import ContestService from 'services/contest.service'
import ToastService from 'services/toast.service'

import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import PhatButton from 'components/formElements/PhatButton'
import PhatDropdown from 'components/formElements/PhatDropdown'
import LineLoader from 'components/elements/LineLoader'
import Editor from 'components/formElements/richText/Editor'
import { emptyTextArea } from 'types/editor'

import { DeckCardInfoType } from 'types/search'

import styles from './contestSubmission.module.scss'

type Props = {
  open: boolean
  onClose: () => void
  threadId: string | number
}

const ContestSubmissionOverlay = ({ open, onClose, threadId }: Props) => {
  const router = useRouter()

  const [{ tbId: userId }] = useCookies(['tbId'])

  const [loading, setLoading] = useState(false)
  const [usersDecks, setUsersDecks] = useState<DeckCardInfoType[]>([])
  const [submissionDeck, setSubmissionDeck] = useState<DeckCardInfoType>()
  const [editedDeckDescription, setEditedDeckDescription] = useState(emptyTextArea)
  const [fetchingDescription, setFetchingDescription] = useState(false)

  const handleCreateSubmission = async () => {
    if (!submissionDeck) {
      ToastService.create('Choose a deck to submit', 'Contest submission', 'warning')
      return
    }

    if (editedDeckDescription === emptyTextArea) {
      ToastService.create('Deck summary needed for submission', 'Contest submission', 'warning')
      return
    }

    setLoading(true)

    try {
      await DeckService.update(submissionDeck.id, { description: editedDeckDescription })
      await ContestService.create({ ownerId: userId, parentPostId: Number(threadId), deckId: submissionDeck.id })

      ReactGA.event({ category: 'Contest', action: 'Contest submission created' })

      ToastService.create(`${submissionDeck.name} submitted`, 'Contest submission', 'success')

      onClose()
      router.push(`/contest/${threadId}/`)
    } catch (err) {
      ToastService.create(err, 'Contest submission', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleSetDeckSubmission = (deck: DeckCardInfoType) => {
    setSubmissionDeck(deck)
    setFetchingDescription(true)

    DeckService.details(deck.id)
      .then(res => setEditedDeckDescription(res.description === '' ? emptyTextArea : res.description))
      .catch(err =>
        ToastService.create(
          `Unable to fetch existing deck description for ${deck.name}`,
          'Contest submission',
          'error',
        ),
      )
      .finally(() => setFetchingDescription(false))
  }

  useEffect(() => {
    if (!open) return
    if (!userId) return

    setLoading(true)

    DeckService.listV3({ ownerId: userId })
      .then(response => setUsersDecks(response.results))
      .catch(() => ToastService.create('Unable to fetch decks', 'Contest submissions', 'error'))
      .finally(() => setLoading(false))
  }, [open])

  return (
    <GlobalOverlayStack open={open} onClose={onClose} className={styles.container}>
      {loading && <LineLoader />}
      {!loading && (
        <div className={styles.content}>
          <h3>Submit to contest</h3>
          <PhatDropdown
            className={styles.decks}
            inlineHeader
            header="Choose one of your decks"
            value={submissionDeck?.id}
            placeholder="Choose one of your decks"
            options={usersDecks.map(deck => ({
              label: deck.name,
              id: `${deck.id}`,
              onClick: () => handleSetDeckSubmission(deck),
            }))}
          />

          <Editor
            header="Deck description"
            value={editedDeckDescription}
            onChange={setEditedDeckDescription}
            placeholder="This deck does so many cool things, this is what they are..."
            disable={fetchingDescription}
          />
          <div className={styles.controls}>
            <PhatButton color="green" onClick={handleCreateSubmission}>
              Create contest submission
            </PhatButton>
          </div>
        </div>
      )}
    </GlobalOverlayStack>
  )
}

export default ContestSubmissionOverlay
